import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import Typography from "@mui/material/Typography";
import PropTypes from 'prop-types';
import { Box, Grid, Tabs, Tab } from "@mui/material";
import CircularProgress from '@mui/material/CircularProgress';
import { ContentBoxWrapper, DocumentBoxWrapper } from "../../styles/theme";
import DocumentList from "../../components/DocumentList/DocumentList";
import ContactList from "../../components/Contacts/ContactsList";
import ActivityFeed from "../../components/Notes/ActivityFeed";
import { projectManageAxios as axios } from '../../utils/AxiosUtility';
import SiteInfo from "../../components/SiteInfo";
import CurrentDesignDisplay from "../../components/DocumentList/CurrentDesignDisplay";
import { useLocation } from 'react-router-dom';

import { PreSalesTask } from "../../components/ProjectManagement/onboarding/PreSalesTask";
import { SitePrepTask } from "../../components/ProjectManagement/siteprep/SitePrepTask";
import { CoordinationTask } from "../../components/ProjectManagement/coordination/CoordinationTask";

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ padding: '10px' }}>
                    <Typography component={"span"}>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function tabProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export function ProjectManageMainPage() {
    let location = useLocation();
    const [loading, setLoading] = useState(false);
    const [tabValue, setTabValue] = new useState(0);
    const [obSubmit, setObSubmit] = useState(false);
    const [siteInfoData, setSiteInfoData] = useState({});
    const isPreSale = location.state?.step === "PreSales";

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        if (location.state != null) {
            location.state.tab = newValue;
        }
    };

    const [fetchDesignData, setFetchDesignData] = useState(false);
    // eslint-disable-next-line
    const [searchParams, setSearchParams] = useSearchParams();

    const handleDesignDataFetch = () => {
        setFetchDesignData(false);
    }

    const gridItemProps = {
        xs: 4,
        container: true,
        justifyContent: "center",
    };

    const handleObSubmit = (state) => {
        setObSubmit(state);
        if (state === false) {
            setFetchDesignData(true);
        }
    }

    const fetchData = () => {
        var endpointString = 'api/Projects/sptask/';
        if (isPreSale) {endpointString = 'api/PreSales/pstask/'}
        const endpoint = endpointString + searchParams.get("id");
        setLoading(true);
        axios.get(endpoint)
            .then((response) => {
                setFetchDesignData(true);
                setLoading(false);
            })
            .catch((error) => {
                alert("Error retreiving data.");
            });
    }

    useEffect(() => {
        fetchData();
        fetchSiteInfoData();

        if (location.state != null) {
            setTabValue(location.state.tab);
        }
        // eslint-disable-next-line
    }, []);
    
      const fetchSiteInfoData = () => {
        const endpoint = isPreSale ? `/api/PreSales/siteInfo/${searchParams.get("id")}` : `/api/Onboarding/siteInfo/${searchParams.get("id")}`
        axios
          .get(endpoint)
          .then((response) => {
            setSiteInfoData(response.data);
          })
          .catch((error) => {
            console.error(error);
            if (error.message) {
              alert(error.message);
            } else {
              alert("Error fetching site info.");
            }
          });
      };

    return (
        <>
            <AuthenticatedTemplate>
                {loading ?
                    <Box height="100%" justifyContent="center" alignContent="center">
                        <CircularProgress variant="indeterminate" />
                    </Box>
                    :
                    <>
                        <Grid
                            container
                            rowSpacing={0}
                            style={{ padding: "30px" }}
                        >
                            <Grid item {...gridItemProps}>
                                <ContentBoxWrapper elevation={2} style={{ paddingBottom: "5%" }}>
                                    <SiteInfo preleadId={isPreSale && searchParams.get("id")}/>
                                </ContentBoxWrapper>
                            </Grid>

                            <Grid item {...gridItemProps}>
                                <ContentBoxWrapper elevation={2} style={{ paddingBottom: "5%" }}>
                                    <ActivityFeed 
                                        projectId={!isPreSale && searchParams.get("id")} 
                                        preleadId={isPreSale && searchParams.get("id")}
                                    />
                                </ContentBoxWrapper>
                            </Grid>

                            <Grid item {...gridItemProps}>
                                <ContentBoxWrapper elevation={2} style={{ paddingBottom: "5%" }}>
                                    <ContactList preleadId={isPreSale && searchParams.get("id")}/>
                                </ContentBoxWrapper>
                            </Grid>

                            <Grid item container xs={6}>
                                <ContentBoxWrapper elevation={2} sx={{ minHeight: "100%" }}>
                                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", }} >
                                            <Tabs value={tabValue} onChange={handleTabChange}>
                                                {isPreSale && <Tab label="Pre-Sales" {...tabProps(0)} />}
                                                {!isPreSale && <Tab label="Site Prep" {...tabProps(0)} />}
                                                {!isPreSale && <Tab label="Coordination" {...tabProps(1)} />}
                                            </Tabs>
                                        </div>
                                    </Box>
                                    <CustomTabPanel value={tabValue} index={0}>
                                        {isPreSale && <PreSalesTask onObTaskSubmit={handleObSubmit} siteData={siteInfoData} />}
                                    </CustomTabPanel>
                                    <CustomTabPanel value={tabValue} index={0}>
                                        {!isPreSale && <SitePrepTask />}
                                    </CustomTabPanel>
                                    <CustomTabPanel value={tabValue} index={1}>
                                        {!isPreSale && <CoordinationTask />}
                                    </CustomTabPanel>
                                </ContentBoxWrapper>
                            </Grid>

                            <Grid item xs={6}>
                                <Grid item container xs={12}>
                                    <DocumentBoxWrapper elevation={2} sx={{ minWidth: "100%" }}>
                                        <DocumentList 
                                            prelimAdded={fetchData} 
                                            onObTaskSubmit={handleObSubmit} 
                                            obSubmit={obSubmit}
                                        />
                                    </DocumentBoxWrapper>
                                </Grid>

                                <Grid item container xs={12}>
                                    <CurrentDesignDisplay
                                        projectId={searchParams.get("id")}
                                        fetchDesignData={fetchDesignData}
                                        onDataFetched={handleDesignDataFetch}
                                        obSubmit={obSubmit}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                }

            </AuthenticatedTemplate>

            <UnauthenticatedTemplate>
                <Typography variant="h6">
                    <center>Please sign-in to see your profile information.</center>
                </Typography>
            </UnauthenticatedTemplate>
        </>
    );
}
